import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useReqdeskContext } from '../ReqdeskProvider'
import { widgetMyTicketsOptions, widgetUserOptions } from '../queries'
import { trackTicket, type TicketListItem } from '../../api-client'
import { saveWidgetEmail, loadWidgetEmail, getTrackingTokens } from '../../storage'
import { en } from '../../i18n/en'
import { ar } from '../../i18n/ar'

const translations: Record<string, Record<string, string>> = { en, ar }

interface MyTicketsViewProps {
  projectId: string
  onSelectTicket: (ticketId: string) => void
  isAuthenticated?: boolean
  userEmail?: string
}

export function MyTicketsView({ projectId, onSelectTicket, isAuthenticated, userEmail }: MyTicketsViewProps) {
  const ctx = useReqdeskContext()
  const savedEmail = loadWidgetEmail(ctx.apiKey)

  // Treat signed/unsigned host identities as pre-authenticated, the same way SubmitTicketView does
  // (FR: signed identity must skip email re-prompt in every ticket-bearing view, not just submit).
  const preAuthedEmail =
    ctx.resolvedIdentityMode.kind === 'signed' || ctx.resolvedIdentityMode.kind === 'unsigned'
      ? ctx.resolvedIdentityMode.email
      : undefined
  const effectiveUserEmail = userEmail ?? preAuthedEmail
  const isHostAuthenticated = (isAuthenticated === true) || preAuthedEmail !== undefined

  const [email, setEmail] = useState(effectiveUserEmail ?? savedEmail ?? '')
  const [rememberMe, setRememberMe] = useState(!!savedEmail)
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(
    isHostAuthenticated && effectiveUserEmail ? effectiveUserEmail : savedEmail,
  )
  const [tokenTickets, setTokenTickets] = useState<TicketListItem[]>([])

  const t = useCallback((key: string): string => {
    if (ctx.translations?.[key]) return ctx.translations[key]
    const lang = translations[ctx.language] ?? translations.en
    return lang[key] ?? key
  }, [ctx.language, ctx.translations])

  const showAllTickets = ctx.showAllTickets ?? false

  // Both OIDC and signed host-app identities let the server scope tickets on its own — OIDC via
  // the JWT `sub` claim, signed identity via the X-Widget-User-Email / X-Widget-User-Signature /
  // X-Widget-User-Timestamp headers (the backend first-or-creates the widget user from the
  // verified email). Email-mode (unauthenticated) still needs the widget-user-by-email lookup
  // to convert email → userId for the filter.
  const skipWidgetUserResolve = isHostAuthenticated && !!effectiveUserEmail
  const { data: widgetUser } = useQuery(widgetUserOptions(projectId, skipWidgetUserResolve ? '' : (showAllTickets ? '' : (submittedEmail ?? ''))))
  const userId = skipWidgetUserResolve ? '' : (widgetUser?.userId ?? '')

  const { data: emailTickets = [], isLoading } = useQuery(widgetMyTicketsOptions(projectId, userId, showAllTickets || skipWidgetUserResolve))

  // Also load token-based tickets (once on mount)
  const [tokensFetched, setTokensFetched] = useState(false)
  useEffect(() => {
    if (tokensFetched) return
    const tokens = getTrackingTokens(ctx.apiKey)
    if (tokens.length === 0) { setTokensFetched(true); return }

    setTokensFetched(true)
    Promise.allSettled(
      tokens.slice(0, 3).map(tk => trackTicket(tk)),
    ).then(results => {
      const tracked: TicketListItem[] = []
      for (const r of results) {
        if (r.status === 'fulfilled') {
          const t = r.value
          tracked.push({
            id: t.id,
            ticketNumber: t.ticketNumber,
            title: t.title,
            status: t.status,
            priority: t.priority,
            createdAt: t.createdAt,
          })
        }
      }
      setTokenTickets(tracked)
    })
  }, [ctx.apiKey, tokensFetched])

  const allTickets = [...emailTickets, ...tokenTickets]

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return

    if (rememberMe) saveWidgetEmail(ctx.apiKey, trimmed)
    setSubmittedEmail(trimmed)
  }

  function formatDate(iso: string): string {
    try { return new Date(iso).toLocaleDateString() } catch { return iso }
  }

  // Show email entry form only if not yet submitted and the host hasn't supplied an identity
  // (OIDC, signed, or unsigned).
  if (!submittedEmail && !isHostAuthenticated) {
    return (
      <div className="rqd-email-form">
        <p>{t('mytickets.emailPrompt')}</p>
        <form onSubmit={handleSubmit}>
          <div className="rqd-form-group">
            <input
              className="rqd-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('mytickets.emailPlaceholder')}
              required
            />
          </div>
          <label className="rqd-checkbox-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            {t('mytickets.rememberMe')}
          </label>
          <button className="rqd-btn rqd-btn-primary" type="submit" style={{ marginTop: 12 }}>
            {t('mytickets.lookup')}
          </button>
        </form>
      </div>
    )
  }

  if (isLoading) {
    return <div className="rqd-loading">{t('mytickets.lookingUp')}</div>
  }

  if (allTickets.length === 0) {
    return (
      <div className="rqd-placeholder">
        <p>{submittedEmail && !widgetUser ? t('mytickets.noAccount') : t('mytickets.noTickets')}</p>
        {submittedEmail && !isHostAuthenticated && (
          <button className="rqd-btn rqd-btn-secondary" style={{ width: 'auto', padding: '8px 20px' }} onClick={() => setSubmittedEmail(null)}>
            {t('tracker.back')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="rqd-ticket-list">
      {allTickets.map(ticket => (
        <div
          key={ticket.id}
          className="rqd-ticket-card"
          onClick={() => onSelectTicket(ticket.id)}
        >
          <div className="rqd-ticket-card-top">
            <span className="rqd-ticket-card-number">{ticket.ticketNumber}</span>
            <span className="rqd-badge">{ticket.status}</span>
          </div>
          <span className="rqd-ticket-card-title">{ticket.title}</span>
          <span className="rqd-ticket-card-date">{formatDate(ticket.createdAt)}</span>
        </div>
      ))}
    </div>
  )
}
